
import { PermissionViewModel } from '../ViewModels';
import {
    ActionResponse,
    ActionStatusHelper,
    DomainTableConfig,
    useAppEnum,
    useAuditEnricher,
    useLayoutLifeCycle,
} from '@/Shared';

import { PermissionHeader } from '../permission-config';
import { DOMAIN_CONFIGS, DomainHookProps } from '@/Shared/Config/domain-config';
import { PermissionModel } from '../Models';

/**
 * usePermissionTableConfig hook
 *
 * Domain-specific layout composition hook for Permission entities.
 * Orchestrates domain data, enrichment, permissions, and UI table configuration.
 *
 * Responsibilities:
 * - Aggregate Permission domain state and enrichment into a single response.
 * - Provide dynamic headers for Permission table.
 * - Map strict Model → ViewModel for UI consumption.
 * - Delegate lifecycle orchestration to useLayoutLifeCycle.
 *
 * @date: March 2, 2026
 * @author: Samano CASTRE
 * 
 * @see DomainHookProps
 * @see DomainTableConfig<PermissionViewModel>
 *
 * @param props Domain-level configuration and permissions
 * @returns DomainTableConfig<PermissionViewModel>
 */
export function usePermissionTableConfig(props: DomainHookProps): DomainTableConfig<PermissionViewModel> {
    const { permissionHook, accessRight } = props;
    const { domainType } = DOMAIN_CONFIGS.Permission;

    const { PermissionEnums } = useAppEnum();

    /**
     * Enrichment layer (audit-based)
     * Converts raw Permission models to enriched view-ready objects
     */
    const { enricherResponse, getEnrichedAudit, } = useAuditEnricher<PermissionModel>();

    /**
     * Combined readiness response
     * Only builds table once all sources succeeded
     */
    const response: ActionResponse = {
        status: ActionStatusHelper.combine(
            enricherResponse.status,
            permissionHook.stateResponse.status
        ),
        error: permissionHook.stateResponse.error
            ?? enricherResponse.error
    };

    /**
    * Delegation to generic layout lifecycle.
    */
    const lifeCycleHook = useLayoutLifeCycle({
        response,
        domainType,
        props,
        entityHook: permissionHook,

        /**
         * Header configuration for Property table.
         */
        headers: PermissionHeader.build(PermissionEnums),

        /**
         * Strict Model → ViewModel transformation.
         */
        toViewModelFn: (permission: PermissionModel, isDisplaying: boolean) =>
            new PermissionViewModel({
                permission: getEnrichedAudit(permission),
                viewEnums: PermissionEnums,
                accessRight,
                isDisplaying
            })
    });

    return lifeCycleHook;
}